import { useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

/**
 * Theme-aware chat colors hook.
 * Spreads useMarketplaceColors() and overrides the chat-specific keys, so it is a
 * drop-in for every key the old static CHAT_COLORS exposed.
 */
export function useChatColors() {
  const mc = useMarketplaceColors();
  const { semantic, components, colors } = useThemeColors();
  return useMemo(() => ({
    ...mc,
    messageBubbleReceived: components.chat.receivedBubble,
    textLabel: colors.text.imageSubtitle,
    textTimestamp: components.chat.timestamp,
    borderLight: semantic.border.tertiary,
    accentBlueDark: semantic.interactive.primary,
    buttonInactive: semantic.interactive.disabled,
    avatarBackground: components.avatar.background,
  }), [mc, semantic, components, colors]);
}


/**
 * Theme-aware chat styles hook.
 * Replaces the old static chatStyles export.
 */
export function useChatStyles() {
  const cc = useChatColors();
  return useMemo(() => StyleSheet.create({
    // Date Separator
    dateSeparator: { alignItems: 'center', paddingVertical: 20 },
    dateSeparatorText: { fontSize: 12, fontWeight: '300', color: cc.textSecondary, lineHeight: 18 },

    // Message Groups
    messageGroup: { marginBottom: 16 },
    messagesInGroup: { gap: 16 },
    messagesContainer: { paddingHorizontal: 24 },

    // Sent Message
    sentMessageContainer: { alignItems: 'flex-end' },
    sentMessageBubble: {
      backgroundColor: cc.accentBlueDark,
      borderTopLeftRadius: 6, borderTopRightRadius: 2, borderBottomLeftRadius: 6, borderBottomRightRadius: 6,
      paddingHorizontal: 12, paddingVertical: 6, maxWidth: '80%', minWidth: 100, gap: 1,
    },
    messageContent: { gap: 2 },
    messageHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
    sentMessageLabel: { fontSize: 12, fontWeight: '500', color: cc.textLabel, lineHeight: 18, flexShrink: 0 },
    sentMessageText: { fontSize: 14, fontWeight: '400', color: cc.textPrimary, lineHeight: 21 },

    // Received Message
    receivedMessageContainer: { flexDirection: 'row', alignItems: 'flex-start', gap: 6 },
    userAvatar: { width: 26, height: 26, borderRadius: 13, backgroundColor: cc.avatarBackground, alignItems: 'center', justifyContent: 'center' },
    userAvatarText: { fontSize: 12, fontWeight: '600', color: cc.textPrimary, textTransform: 'uppercase' },
    receivedMessageBubble: {
      backgroundColor: cc.messageBubbleReceived,
      borderTopLeftRadius: 2, borderTopRightRadius: 6, borderBottomLeftRadius: 6, borderBottomRightRadius: 6,
      paddingHorizontal: 12, paddingVertical: 6, maxWidth: '80%', minWidth: 100, gap: 1,
    },
    receivedMessageLabel: { fontSize: 12, fontWeight: '500', color: cc.textSecondary, lineHeight: 18, flexShrink: 0 },
    receivedMessageText: { fontSize: 14, fontWeight: '400', color: cc.textPrimary, lineHeight: 21 },

    // Timestamp
    timestampContainer: { alignItems: 'flex-end' },
    timestampText: { fontSize: 12, fontWeight: '300', color: cc.textTimestamp, lineHeight: 18 },

    // Menu Button
    menuButton: { width: 18, height: 18, alignItems: 'center', justifyContent: 'center' },

    // Input Bar
    inputBar: { flexDirection: 'row', alignItems: 'center', gap: 16, paddingHorizontal: 24, paddingTop: 20, borderTopWidth: 1, borderTopColor: cc.dividerGray },
    attachButton: { paddingTop: 5 },
    textInput: { flex: 1, fontSize: 14, fontWeight: '400', color: cc.textPrimary, lineHeight: 21, paddingVertical: 6, maxHeight: 100 },
    sendButton: { width: 34, height: 34, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
    sendButtonActive: { backgroundColor: cc.accentBlueDark },
    sendButtonInactive: { backgroundColor: cc.buttonInactive },

    // Chat Header
    chatHeader: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', paddingHorizontal: 24, paddingTop: 20, paddingBottom: 18, borderBottomWidth: 1, borderBottomColor: cc.dividerGray, gap: 20 },
    chatHeaderContent: { flex: 1, flexDirection: 'row', alignItems: 'flex-start', gap: 12 },
    chatThumbnail: { width: 40, height: 40, borderRadius: 20, borderWidth: 0.45, borderColor: cc.borderLight },
    chatThumbnailPlaceholder: { backgroundColor: cc.uploadAreaBackground, alignItems: 'center', justifyContent: 'center' },
    chatHeaderTextContainer: { flex: 1, gap: 2 },
    chatTitle: { fontSize: 14, fontWeight: '500', color: cc.textPrimary, lineHeight: 21 },
    chatSubtitle: { fontSize: 12, fontWeight: '300', color: cc.textSecondary, lineHeight: 18 },

    // Chat Content
    chatContent: { flex: 1 },
    chatContentContainer: { flexGrow: 1 },

    // Empty State
    emptyState: { alignItems: 'center', paddingHorizontal: 24, paddingVertical: 44 },
    emptyIconContainer: { marginBottom: 18 },
    emptyTitle: { fontSize: 16, fontWeight: '600', color: cc.textPrimary, lineHeight: 24, textAlign: 'center', marginBottom: 2 },
    emptySubtitle: { fontSize: 14, fontWeight: '400', color: cc.textMuted, lineHeight: 21, textAlign: 'center', width: 240 },

    // Quick Replies
    quickRepliesContainer: { alignItems: 'flex-end', paddingHorizontal: 24, gap: 14, marginBottom: 24 },
    quickReplyButton: { borderWidth: 1, borderColor: cc.accentBlueDark, borderRadius: 6, paddingHorizontal: 12, paddingVertical: 6 },
    quickReplyText: { fontSize: 14, fontWeight: '500', color: cc.accentBlue, lineHeight: 21, textAlign: 'center' },
  }), [cc]);
}
